import { defineComponent as _defineComponent } from 'vue'
import ConfirmDeleteModal from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import LandSubdivisionLotDetailModal from "@/v2/components/ui/modals/LandSubdivisionLotDetailModal.vue";
  import LotDetailModal from "@/v2/components/ui/modals/LotDetailModal.vue";
  import router from "@/router";
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
  import { useStore } from "vuex";
  import {
    Bed,
    Bath,
    Car,
    Expand,
    Maximize2,
    MoveHorizontal,
    Home,
    ChevronRight,
    Info,
    Map as MapIcon,
  } from "lucide-vue-next";
  import { UserRoles } from "@/enums/UserRoles";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import Hammer from "hammerjs";
  import type HammerInput from "hammerjs";
  import { handleDragging, startDragging, stopDragging } from "@/helpers/zoom";
  import {
    getDeviceZoomCenter,
    getCameraScroll,
    getDeviceZoomLevels,
    getMinimumCoverZoom,
    getViewThresholds,
  } from "@/helpers/masterplan/zoomViewport";
  import { getBoundaryLabelCenter } from "@/helpers/masterplan/boundaryLabelCenter";
  import { getStageStatusClass } from "@/helpers/status";
  import {
    getMasterplanLotStatusClass,
    getMasterplanLotStatusLabel,
    getMasterplanCssVars,
    getMasterplanStatusBadgeStyle,
    MASTERPLAN_STATUS_COLORS,
  } from "@/v2/helpers/masterplanLotStatus";
  import { LotStatus } from "@/enums/LotStatus";
  import { EstateType } from "@/enums/EstateType";
  import { EstateAddon } from "@/enums/EstateAddon";
  import { InteractiveMasterplanInterface } from "@/types/InteractiveMasterplanInterface";
  import {
    calculatePolygonCentroid,
    calculateBoundingBoxCenter,
    getLotLabelGap,
    LOT_NUMBER_FONT_SIZE,
    LOT_SIZE_FONT_SIZE,
    LOT_SIZE_DEFAULT_SCALE,
  } from "@/helpers/lotNumber/lotCentroidCalculator";

  const mobileLayoutWidth = 680;
  type PreviewBounds = {
    x: number;
    y: number;
    width: number;
    height: number;
  };

  type ClickZoomTarget = "estate" | "stage" | "lot";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'InteractiveMasterplan',
  props: {
    role: { type: String, default: () => UserRoles.LANDOWNER },
    viewType: { type: String, default: "" },
    isEmbedded: { type: Boolean, default: false },
    mapPage: { type: String, default: "" },
    builderView: { type: Boolean, required: false },
    estateId: { type: String, required: false, default: undefined },
    matchingHouseDesignId: {
      type: String,
      required: false,
      default: undefined,
    },
    matchingFacadeId: { type: String, required: false, default: undefined },
    initialMasterplan: { type: Object, default: undefined },
    estateName: { type: String, required: false, default: "" },
  },
  emits: [
    "updateZoom",
    "map-ready",
    "lot-modal-visibility-change",
    "mobile-map-expanded-change",
  ],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const emit = __emit;
  const store = useStore();
  const { isDesktop } = useIsDesktop(mobileLayoutWidth);
  const userId = router.currentRoute.value.params.userId;
  const estateId = computed(() => getEstateId());
  const masterplan = ref<InteractiveMasterplanInterface | null>(null);
  const svgContent = ref<string>("");
  const overlayViewBox = ref<string>("");
  const zoom = ref(1);
  const containerClientWidth = ref(0);
  const containerClientHeight = ref(0);
  const resizeObserver = ref<ResizeObserver | null>(null);
  const isDragging = ref(false);
  const wasDragging = ref(false);
  const mapWrapper = ref<HTMLElement | null>(null);
  const svgContainerMap = ref<HTMLElement | null>(null);
  const svgLayers = ref<HTMLElement | null>(null);
  const svgStartPosition = ref({ x: 0, y: 0 });
  const svgStartScrollPosition = ref({ x: 0, y: 0 });
  const showConfirmDeleteModal = ref(false);
  const showLotModal = ref(false);
  const lotsLoading = ref(true);
  const hoverLotId = ref<string>("");
  const selectedLotId = ref<string>("");
  const selectedStageId = ref<string>("");
  const pulseTimeoutId = ref<number | null>(null);
  const stagePulseTimeoutId = ref<number | null>(null);
  const currentSidebarView = ref<"stages" | "lots" | "lot-matching">("stages");
  const hammerManager = ref<HammerManager | null>(null);
  const isPinching = ref(false);
  const pinchStartZoom = ref(1);
  const pinchStartScrollX = ref(0);
  const pinchStartScrollY = ref(0);
  const pinchContentFocalX = ref(0);
  const pinchContentFocalY = ref(0);
  const pinchContainerRect = ref<DOMRect | null>(null);
  const mobileMapExpanded = ref(false);
  const viewportWidth = ref(
    typeof window !== "undefined" ? window.innerWidth : mobileLayoutWidth
  );
  const viewportHeight = ref(
    typeof window !== "undefined" ? window.innerHeight : 800
  );

  const masterplanCssVars = getMasterplanCssVars();

  const isPhoneLandscape = computed(
    () =>
      viewportWidth.value > viewportHeight.value &&
      viewportHeight.value <= 520 &&
      viewportWidth.value <= 990
  );

  const activeZoomLevels = computed(() =>
    getDeviceZoomLevels(
      masterplan.value?.zoomLevels,
      !isDesktop.value,
      isPhoneLandscape.value
    )
  );

  const wheelZoomTarget = ref(1);
  const wheelZoomFocalX = ref(0);
  const wheelZoomFocalY = ref(0);
  const wheelAnimFrame = ref<number | null>(null);
  const navAnimFrame = ref<number | null>(null);
  let liveZoom = 1;
  let pinchAnimFrame: number | null = null;
  let pendingPinchZoom = 1;
  let pendingPinchFocalX = 0;
  let pendingPinchFocalY = 0;
  const stages = ref<any[]>([]);
  const stageStatusMap = ref<Record<string, string>>({});
  const selectedStage = ref<any>(null);
  const selectedLot = ref<any>(null);
  const selectedPoi = ref<any>(null);
  const showPackagesOnly = ref(false);
  const lotPreviewUrl = ref("");

  watch(
    showLotModal,
    (isVisible) => emit("lot-modal-visibility-change", isVisible),
    { immediate: true }
  );

  const props = __props;

  const isLandSubdivisionBuyerView = computed(() => {
    if (props.role !== UserRoles.CUSTOMER) return false;

    const estate = masterplan.value?.estateId;

    return (
      typeof estate === "object" && estate?.type === EstateType.LAND_SUBDIVISION
    );
  });

  const clearLotPreview = () => {
    if (lotPreviewUrl.value) URL.revokeObjectURL(lotPreviewUrl.value);

    lotPreviewUrl.value = "";
  };

  const getPreviewLotBounds = (lot: any): PreviewBounds | null => {
    if (Array.isArray(lot?.polygonPoints) && lot.polygonPoints.length > 0) {
      const xs = lot.polygonPoints.map((point: any) => Number(point.x));
      const ys = lot.polygonPoints.map((point: any) => Number(point.y));

      if ([...xs, ...ys].every(Number.isFinite)) {
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);

        return {
          x: minX,
          y: minY,
          width: Math.max(1, Math.max(...xs) - minX),
          height: Math.max(1, Math.max(...ys) - minY),
        };
      }
    }

    const bounds = lot?.boundingBox;

    if (!bounds) return null;

    const parsedBounds = {
      x: Number(bounds.x),
      y: Number(bounds.y),
      width: Number(bounds.width),
      height: Number(bounds.height),
    };

    return Object.values(parsedBounds).every(Number.isFinite) &&
      parsedBounds.width > 0 &&
      parsedBounds.height > 0
      ? parsedBounds
      : null;
  };

  const copyRenderedSvgStyles = (source: SVGElement, clone: SVGElement) => {
    const sourceElements = [
      source,
      ...Array.from(source.querySelectorAll("*")),
    ];
    const cloneElements = [clone, ...Array.from(clone.querySelectorAll("*"))];
    const properties = [
      "display",
      "visibility",
      "opacity",
      "fill",
      "fill-opacity",
      "stroke",
      "stroke-width",
      "stroke-opacity",
      "stroke-linecap",
      "stroke-linejoin",
      "filter",
      "font-family",
      "font-size",
      "font-weight",
      "letter-spacing",
      "paint-order",
      "text-anchor",
    ];

    sourceElements.forEach((element, index) => {
      const target = cloneElements[index] as SVGElement | undefined;

      if (!target) return;

      const computedStyle = window.getComputedStyle(element);

      properties.forEach((property) => {
        const value = computedStyle.getPropertyValue(property);

        if (value) target.style.setProperty(property, value);
      });
    });
  };

  const createLotPreview = (lot: any) => {
    clearLotPreview();

    if (!mapWrapper.value || !overlayViewBox.value) return;

    const sourceBackground = mapWrapper.value.querySelector(
      ".svg-background-content svg"
    ) as SVGSVGElement | null;
    const sourceOverlay = mapWrapper.value.querySelector(
      ".svg-interactive-overlay"
    ) as SVGSVGElement | null;
    const lotBounds = getPreviewLotBounds(lot);
    const viewBoxParts = overlayViewBox.value.split(/[ ,]+/).map(Number);

    if (
      !sourceBackground ||
      !sourceOverlay ||
      !lotBounds ||
      viewBoxParts.length !== 4 ||
      !viewBoxParts.every(Number.isFinite)
    ) {
      return;
    }

    const [masterplanX, masterplanY, masterplanWidth, masterplanHeight] =
      viewBoxParts;
    const previewAspectRatio = 4 / 5;
    let previewWidth = Math.max(
      lotBounds.width * 2.7,
      lotBounds.height * 2.7 * previewAspectRatio,
      masterplanWidth * 0.09,
      masterplanHeight * 0.09 * previewAspectRatio
    );

    previewWidth = Math.min(
      previewWidth,
      masterplanWidth,
      masterplanHeight * previewAspectRatio
    );
    const previewHeight = previewWidth / previewAspectRatio;

    const centerX = lotBounds.x + lotBounds.width / 2;
    const centerY = lotBounds.y + lotBounds.height / 2;
    const previewX = Math.min(
      masterplanX + masterplanWidth - previewWidth,
      Math.max(masterplanX, centerX - previewWidth / 2)
    );
    const previewY = Math.min(
      masterplanY + masterplanHeight - previewHeight,
      Math.max(masterplanY, centerY - previewHeight / 2)
    );

    const previewSvg = sourceBackground.cloneNode(true) as SVGSVGElement;

    copyRenderedSvgStyles(sourceBackground, previewSvg);
    const overlayClone = sourceOverlay.cloneNode(true) as SVGSVGElement;

    copyRenderedSvgStyles(sourceOverlay, overlayClone);
    Array.from(overlayClone.children).forEach((child) =>
      previewSvg.appendChild(child)
    );

    previewSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    previewSvg.setAttribute(
      "viewBox",
      `${previewX} ${previewY} ${previewWidth} ${previewHeight}`
    );
    previewSvg.setAttribute("width", "800");
    previewSvg.setAttribute("height", "1000");
    previewSvg.setAttribute("preserveAspectRatio", "xMidYMid slice");

    previewSvg.querySelectorAll(".lot-selected.lot-rect").forEach((element) => {
      const selectedShape = element as SVGElement;

      selectedShape.style.setProperty("fill", "rgba(48, 75, 112, 0.2)");
      selectedShape.style.setProperty("stroke", "#304b70");
      selectedShape.style.setProperty("stroke-width", "4px");
    });

    const previewBlob = new Blob(
      [new XMLSerializer().serializeToString(previewSvg)],
      {
        type: "image/svg+xml;charset=utf-8",
      }
    );

    lotPreviewUrl.value = URL.createObjectURL(previewBlob);
  };

  watch([showLotModal, selectedLot], async ([isVisible, lot]) => {
    if (!isVisible || !lot || !isLandSubdivisionBuyerView.value) {
      clearLotPreview();
      return;
    }

    await nextTick();
    createLotPreview(lot);
  });

  const hasLandSubdivisionPackagesFeature = computed(() => {
    if (!isLandSubdivisionBuyerView.value) return false;

    const estate = masterplan.value?.estateId;

    return (
      typeof estate === "object" &&
      estate?.addons?.includes(EstateAddon.HL_PACKAGES)
    );
  });

  const isCustomerMobileMapLayout = computed(
    () =>
      props.role === UserRoles.CUSTOMER &&
      (!isDesktop.value || isPhoneLandscape.value)
  );

  const shouldCoverMobileMap = computed(
    () => isCustomerMobileMapLayout.value && mobileMapExpanded.value
  );

  const getBaseSvgWidth = (
    containerWidth: number,
    containerHeight: number,
    viewBoxWidth: number,
    viewBoxHeight: number
  ) => {
    if (!shouldCoverMobileMap.value) return containerWidth;

    const aspectRatio = viewBoxHeight / viewBoxWidth;

    return Math.max(containerWidth, containerHeight / aspectRatio);
  };

  const onSelectLot = (
    lot: any,
    selectedHouseDesign?: any,
    selectedFacade?: any
  ) => {
    showLotModal.value = false;
    const houseDesign =
      selectedHouseDesign ?? lot?.lotId?.builders?.[0]?.housesIds?.[0]?.houseId;
    const houseId = houseDesign?._id ?? houseDesign;
    const lotId = lot?.lotId?._id;
    const assignedFacade = selectedFacade ?? getLotAssignedFacade(lot);
    const facadeId = assignedFacade?._id;

    if (!houseId) {
      console.warn("onSelectLot: no house design found for lot", lotId);
      return;
    }

    router
      .push({
        name: "HouseInteriorsV2",
        params: { houseId },
        query: {
          ...(estateId.value ? { estateId: estateId.value } : {}),
          ...(lotId ? { lotId } : {}),
          ...(facadeId ? { facade: facadeId } : {}),
          ...(props.isEmbedded ? { embed: "true" } : {}),
        },
      })
      .catch((err: any) => {
        if (
          err?.name === "ChunkLoadError" ||
          err?.message?.includes("Loading chunk")
        ) {
          window.location.href = router.resolve({
            name: "HouseInteriorsV2",
            params: { houseId },
            query: {
              ...(estateId.value ? { estateId: estateId.value } : {}),
              ...(lotId ? { lotId } : {}),
              ...(facadeId ? { facade: facadeId } : {}),
              ...(props.isEmbedded ? { embed: "true" } : {}),
            },
          }).href;
        }
      });
  };

  const getEstateId = () => {
    if (props.estateId) {
      return props.estateId;
    } else if (props.role === UserRoles.LANDOWNER) {
      return store.state.user.selectedEstateId;
    } else {
      return router.currentRoute.value.params.estateId;
    }
  };

  const getSvgLayersStyle = (zoomLevel: number) => {
    if (!overlayViewBox.value || !containerClientWidth.value) {
      return {
        width: `${100 * zoomLevel}%`,
        height: `${100 * zoomLevel}%`,
      };
    }

    const [, , vbW, vbH] = overlayViewBox.value.split(" ").map(Number);
    const baseWidth = getBaseSvgWidth(
      containerClientWidth.value,
      containerClientHeight.value,
      vbW,
      vbH
    );
    const pxW = baseWidth * zoomLevel;
    const pxH =
      zoomLevel < 1
        ? pxW * (vbH / vbW)
        : Math.max(containerClientHeight.value, pxW * (vbH / vbW));

    return {
      width: shouldCoverMobileMap.value ? `${pxW}px` : `${100 * zoomLevel}%`,
      height: `${pxH}px`,
    };
  };

  const applySvgLayersStyle = (zoomLevel = liveZoom) => {
    if (!svgLayers.value) return;

    const style = getSvgLayersStyle(zoomLevel);

    svgLayers.value.style.width = style.width;
    svgLayers.value.style.height = style.height;
  };

  const shouldUseMobilePinchPreview = () =>
    !isDesktop.value || isPhoneLandscape.value;

  const resetSvgLayersTransform = () => {
    if (!svgLayers.value) return;

    svgLayers.value.style.transform = "translateZ(0)";
    svgLayers.value.classList.remove("mobile-pinch-zooming");
  };

  const applyMobilePinchPreview = (
    targetZoom: number,
    focalX: number,
    focalY: number
  ) => {
    if (!svgContainerMap.value || !svgLayers.value) return;

    const visualScale = targetZoom / pinchStartZoom.value;
    const startContentFocalX = pinchContentFocalX.value * pinchStartZoom.value;
    const startContentFocalY = pinchContentFocalY.value * pinchStartZoom.value;
    const translateX =
      focalX + pinchStartScrollX.value - startContentFocalX * visualScale;
    const translateY =
      focalY + pinchStartScrollY.value - startContentFocalY * visualScale;

    liveZoom = targetZoom;
    wheelZoomTarget.value = targetZoom;
    svgLayers.value.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${visualScale})`;
  };

  const restoreScrollForFocal = (
    container: HTMLElement,
    contentFocalX: number,
    contentFocalY: number,
    zoomLevel: number,
    focalX: number,
    focalY: number
  ) => {
    container.scrollLeft = contentFocalX * zoomLevel - focalX;
    container.scrollTop = contentFocalY * zoomLevel - focalY;
  };

  const restoreScrollForFocalAfterPatch = (
    container: HTMLElement,
    contentFocalX: number,
    contentFocalY: number,
    getZoomLevel: () => number,
    focalX: number,
    focalY: number
  ) => {
    nextTick(() => {
      requestAnimationFrame(() => {
        restoreScrollForFocal(
          container,
          contentFocalX,
          contentFocalY,
          getZoomLevel(),
          focalX,
          focalY
        );
      });
    });
  };

  const scheduleMobilePinchPreview = (
    targetZoom: number,
    focalX: number,
    focalY: number
  ) => {
    pendingPinchZoom = targetZoom;
    pendingPinchFocalX = focalX;
    pendingPinchFocalY = focalY;

    if (pinchAnimFrame !== null) return;

    pinchAnimFrame = requestAnimationFrame(() => {
      pinchAnimFrame = null;
      applyMobilePinchPreview(
        pendingPinchZoom,
        pendingPinchFocalX,
        pendingPinchFocalY
      );
    });
  };

  const availableStatuses = [LotStatus.AVAILABLE.toLowerCase()];

  const stagesWithCounts = computed(() => {
    if (!stages.value.length) return [];

    const lots = masterplan.value?.lots || [];

    return stages.value
      .map((stage) => {
        const stageLots = lots.filter((lot: any) => {
          const lotStageId = lot.lotId?.stageId?._id ?? lot.lotId?.stageId;

          return String(lotStageId) === String(stage._id);
        });
        const availableLots = stageLots.filter((lot: any) =>
          availableStatuses.includes((lot.lotId?.status || "").toLowerCase())
        ).length;

        return { ...stage, totalLots: stageLots.length, availableLots };
      })
      .filter((s) => s.availableLots > 0);
  });

  const lotsForSelectedStage = computed(() => {
    if (!selectedStage.value || !masterplan.value?.lots) return [];

    return masterplan.value.lots
      .filter((lot: any) => {
        const lotStageId = lot.lotId?.stageId?._id ?? lot.lotId?.stageId;

        return String(lotStageId) === String(selectedStage.value._id);
      })
      .slice()
      .sort((a: any, b: any) => {
        const extractNum = (title: any) =>
          parseFloat(String(title ?? "").replace(/^[^\d-]*/, ""));
        const aNum = extractNum(a.lotId?.title);
        const bNum = extractNum(b.lotId?.title);
        const aIsNum = isFinite(aNum);
        const bIsNum = isFinite(bNum);

        if (aIsNum && bIsNum) return aNum - bNum;

        if (aIsNum) return -1;

        if (bIsNum) return 1;

        return String(a.lotId?.title ?? "").localeCompare(
          String(b.lotId?.title ?? "")
        );
      });
  });

  const selectedStageLotsCount = computed(
    () => lotsForSelectedStage.value.length
  );

  const selectedStageAvailableCount = computed(
    () =>
      lotsForSelectedStage.value.filter((lot: any) =>
        availableStatuses.includes((lot.lotId?.status || "").toLowerCase())
      ).length
  );

  const availableLotsForSelectedStage = computed(() =>
    lotsForSelectedStage.value.filter((lot: any) =>
      availableStatuses.includes((lot.lotId?.status || "").toLowerCase())
    )
  );

  const displayedLandSubdivisionLots = computed(() => {
    if (!showPackagesOnly.value) return availableLotsForSelectedStage.value;

    return availableLotsForSelectedStage.value.filter(
      (lot: any) => getLotPackageCount(lot) > 0
    );
  });

  const selectedStageMeta = computed(() => {
    if (!selectedStageId.value || !masterplanStages.value.length) return null;

    return (
      masterplanStages.value.find((s: any) => {
        const stageIdValue =
          typeof s.stageId === "object" ? s.stageId._id : s.stageId;

        return String(stageIdValue) === String(selectedStageId.value);
      }) || null
    );
  });

  const processedSvgForBackground = computed(() => {
    if (!svgContent.value) return "";

    let svg = svgContent.value;

    if (overlayViewBox.value && !svg.includes("viewBox")) {
      svg = svg.replace("<svg", `<svg viewBox="${overlayViewBox.value}"`);
    }

    svg = svg.replace(/fill="#4[Ee]612[Ff]"/g, 'fill="transparent"');
    return svg;
  });

  const backfillStatusBarPaths = (svgText: string) => {
    if (!masterplan.value?.lots?.length) return;

    const anyMissing = masterplan.value.lots.some((l: any) => !l.statusBarPath);

    if (!anyMissing) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const STATUS_BAR_COLOR = "#4e612f";

    const barMap = new Map<string, string>();
    const rects = Array.from(doc.getElementsByTagName("rect"));

    rects.forEach((rect) => {
      const x = parseFloat(rect.getAttribute("x") || "0");
      const y = parseFloat(rect.getAttribute("y") || "0");
      const w = parseFloat(rect.getAttribute("width") || "0");
      const h = parseFloat(rect.getAttribute("height") || "0");

      if (w <= 0 || h <= 0) return;

      let sibling = rect.nextSibling;

      while (sibling && sibling.nodeType === 3) sibling = sibling.nextSibling;

      if (!sibling || (sibling as Element).tagName?.toLowerCase() !== "path")
        return;

      const pathEl = sibling as Element;

      if (
        (pathEl.getAttribute("fill") || "").toLowerCase() !== STATUS_BAR_COLOR
      )
        return;

      const d = pathEl.getAttribute("d");

      if (!d) return;

      barMap.set(
        `${Math.round(x)},${Math.round(y)},${Math.round(w)},${Math.round(h)}`,
        d
      );
    });

    if (!barMap.size) return;

    masterplan.value.lots.forEach((lot: any) => {
      if (lot.statusBarPath) return;

      const bb = lot.boundingBox;

      if (!bb) return;

      const key = `${Math.round(bb.x)},${Math.round(bb.y)},${Math.round(
        bb.width
      )},${Math.round(bb.height)}`;
      const d = barMap.get(key);

      if (d) lot.statusBarPath = d;
    });
  };

  const getLotPolygonPoints = (lot: any): string =>
    lot.polygonPoints
      ?.map((p: { x: number; y: number }) => `${p.x},${p.y}`)
      .join(" ") ?? "";

  const soldLotStatuses = new Set(
    [LotStatus.SOLD, LotStatus.SETTLED, LotStatus.UNCONDITIONAL].map((status) =>
      status.toLowerCase()
    )
  );

  const isSoldLot = (lot: any) =>
    soldLotStatuses.has(String(lot?.lotId?.status || "").toLowerCase());

  const getLotClasses = (lot: any) => {
    const status = lot.lotId?.status || "Not Released";
    const isSold = isSoldLot(lot);
    const isHovered = hoverLotId.value === lot.lotId?._id;
    const isSelected = selectedLotId.value === lot.lotId?._id;
    const isMatchingMode = currentSidebarView.value === "lot-matching";
    const isCompatible =
      !isMatchingMode || compatibleLotIds.value.has(lot.lotId?._id);

    return [
      getMasterplanLotStatusClass(status),
      "lot-interactive",
      { "lot-clickable": props.role === UserRoles.CUSTOMER && !isSold },
      { "lot-not-clickable": isSold },
      { "lot-hovered": isHovered },
      { "lot-selected": isSelected },
      { "lot-dimmed": isMatchingMode && !isCompatible },
      { "lot-compatible": isMatchingMode && isCompatible },
    ];
  };

  const isStageNowSelling = (stageMeta: any): boolean => {
    const stageIdStr = String(
      typeof stageMeta.stageId === "object"
        ? (stageMeta.stageId as any)._id
        : stageMeta.stageId
    );
    const fill = stageFillFromLots.value[stageIdStr];

    return fill?.var === "--map-estate-selling-2";
  };

  const getStageClasses = (stageId: any) => {
    const stageIdValue = typeof stageId === "object" ? stageId._id : stageId;
    const isSelected = selectedStageId.value === stageIdValue;

    return { "stage-selected": isSelected };
  };

  const getLotHouseDesignEntries = (lot: any): any[] =>
    (lot?.lotId?.builders || []).flatMap((b: any) => b.housesIds || []);

  const getLotPackageCount = (lot: any): number =>
    Array.isArray(lot?.landSubdivisionPackages)
      ? lot.landSubdivisionPackages.length
      : 0;

  const getLotPackageAvailabilityLabel = (lot: any): string => {
    const count = getLotPackageCount(lot);

    return `${count} Home & Land ${
      count === 1 ? "Package" : "Packages"
    } Available`;
  };

  const getLandSubdivisionLotPrice = (lot: any): string => {
    if (lot?.lotId?.hidePrice || !lot?.lotId?.price) return "--";

    return formatPrice(lot.lotId.price);
  };

  const getHouseDesignId = (entry: any): string => {
    const houseDesign = entry?.houseId;

    return String(houseDesign?._id ?? houseDesign ?? "");
  };

  const getUniqueLotHouseDesignEntries = (lot: any): any[] => {
    const seen = new Set<string>();

    return getLotHouseDesignEntries(lot).filter((entry: any) => {
      const id = getHouseDesignId(entry);

      if (!id || seen.has(id)) return false;

      seen.add(id);
      return true;
    });
  };

  const getLotHouseDesign = (lot: any, houseDesignId?: string): any | null => {
    const entries = getUniqueLotHouseDesignEntries(lot);

    if (!houseDesignId) return entries[0]?.houseId ?? null;

    return (
      entries.find(
        (entry: any) => getHouseDesignId(entry) === String(houseDesignId)
      )?.houseId ?? null
    );
  };

  const getLotDesignLabel = (lot: any): string => {
    const entries = getUniqueLotHouseDesignEntries(lot);

    if (entries.length > 1) return "Multiple Designs";

    const houseDesign = entries[0]?.houseId;

    return typeof houseDesign === "object" ? houseDesign?.name ?? "" : "";
  };

  const countLotFacadeOptions = (lot: any, houseDesignId?: string): number => {
    const facadeIds = new Set<string>();
    const facadeNames = new Set<string>();
    const entries = getLotHouseDesignEntries(lot).filter((entry: any) => {
      if (!houseDesignId) return true;

      return getHouseDesignId(entry) === String(houseDesignId);
    });

    entries.forEach((entry: any) => {
      const hd = entry?.houseId;
      const assignedIds: string[] = (entry?.facadeIds || []).map(String);

      if (assignedIds.length) {
        assignedIds.forEach((id) => facadeIds.add(id));
        return;
      }

      if (hd && typeof hd === "object") {
        (hd.facades || []).forEach((facade: any) => {
          const id = facade?._id ? String(facade._id) : "";

          if (id) {
            facadeIds.add(id);
          } else if (facade?.name) {
            facadeNames.add(facade.name);
          }
        });
      }
    });

    return facadeIds.size + facadeNames.size;
  };

  const getLotAssignedFacade = (
    lot: any,
    houseDesignId?: string
  ): any | null => {
    const hd = getLotHouseDesign(lot, houseDesignId);

    if (!hd) return null;

    const houseEntry = getLotHouseDesignEntries(lot).find(
      (h: any) => String(h.houseId?._id ?? h.houseId) === String(hd._id ?? hd)
    );
    const assignedIds: string[] = (houseEntry?.facadeIds || []).map(String);

    if (assignedIds.length) {
      const match = (hd.facades || []).find((f: any) =>
        assignedIds.includes(String(f._id))
      );

      if (match) return match;
    }

    return hd.facades?.[0] ?? null;
  };

  const getLotAssignedFacadeName = (lot: any): string =>
    getLotAssignedFacade(lot)?.name ?? "";

  const getLotFacadeLabel = (lot: any, houseDesignId?: string): string => {
    const facadeOptionsCount = countLotFacadeOptions(lot, houseDesignId);

    if (facadeOptionsCount > 1) return "Multiple Facades";

    let hd: any;

    if (houseDesignId) {
      const entry = getLotHouseDesignEntries(lot).find(
        (h: any) => String(h.houseId?._id ?? h.houseId) === houseDesignId
      );

      hd = entry?.houseId ?? null;
    } else {
      hd = getLotHouseDesign(lot);
    }

    if (!hd) return "";

    const houseEntry = getLotHouseDesignEntries(lot).find(
      (h: any) => String(h.houseId?._id ?? h.houseId) === String(hd._id ?? hd)
    );
    const assignedIds: string[] = (houseEntry?.facadeIds || []).map(String);

    if (assignedIds.length === 1) {
      const match = (hd.facades || []).find((f: any) =>
        assignedIds.includes(String(f._id))
      );

      return match?.name ? `${match.name} Facade` : "";
    }

    if (assignedIds.length > 1 || (hd.facades || []).length > 1) {
      return "Multiple Facades";
    }

    return hd.facades?.[0]?.name ? `${hd.facades[0].name} Facade` : "";
  };
  const getLotAssignedFacadeUrl = (lot: any, houseDesignId?: string): string =>
    getLotAssignedFacade(lot, houseDesignId)?.url ??
    getLotHouseDesign(lot, houseDesignId)?.mainImages?.[0] ??
    "";

  const getLotPackagePrice = (lot: any, houseDesignId?: string): number => {
    const hd = getLotHouseDesign(lot, houseDesignId);

    return hd?.basePrice ?? 0;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const onLotHover = (value: boolean, lotId: string) => {
    hoverLotId.value = value ? lotId : "";
  };

  const easeInOutCubic = (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  const navigateToTarget = (
    targetZoom: number,
    targetScrollLeft: number,
    targetScrollTop: number,
    duration = 550
  ) => {
    if (!svgContainerMap.value) return;

    const container = svgContainerMap.value;

    if (navAnimFrame.value !== null) {
      cancelAnimationFrame(navAnimFrame.value);
      navAnimFrame.value = null;
    }

    if (wheelAnimFrame.value !== null) {
      cancelAnimationFrame(wheelAnimFrame.value);
      wheelAnimFrame.value = null;
    }

    const startZoom = liveZoom;
    const startScrollLeft = container.scrollLeft;
    const startScrollTop = container.scrollTop;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const startTime = performance.now();

    const startFocalX = (startScrollLeft + containerW / 2) / startZoom;
    const startFocalY = (startScrollTop + containerH / 2) / startZoom;
    const targetFocalX = (targetScrollLeft + containerW / 2) / targetZoom;
    const targetFocalY = (targetScrollTop + containerH / 2) / targetZoom;

    const step = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      const e = easeInOutCubic(t);
      const cz = startZoom + (targetZoom - startZoom) * e;

      setZoom(cz);

      const focalX = startFocalX + (targetFocalX - startFocalX) * e;
      const focalY = startFocalY + (targetFocalY - startFocalY) * e;

      container.scrollLeft = cz * focalX - containerW / 2;
      container.scrollTop = cz * focalY - containerH / 2;

      if (t < 1) {
        navAnimFrame.value = requestAnimationFrame(step);
      } else {
        setZoom(targetZoom, { syncReactive: true, emitZoom: true });
        container.scrollLeft = targetScrollLeft;
        container.scrollTop = targetScrollTop;
        navAnimFrame.value = null;
        wheelZoomTarget.value = targetZoom;
      }
    };

    navAnimFrame.value = requestAnimationFrame(step);
  };

  const computeScrollTarget = (
    svgCenterX: number,
    svgCenterY: number,
    targetZoom: number
  ): { left: number; top: number } | null => {
    if (!svgContainerMap.value || !overlayViewBox.value) return null;

    const container = svgContainerMap.value;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const [vbX, vbY, vbW, vbH] = overlayViewBox.value.split(" ").map(Number);

    const baseWidth = getBaseSvgWidth(containerW, containerH, vbW, vbH);
    const contentW = baseWidth * targetZoom;
    const meetScale = contentW / vbW;
    const contentH = vbH * meetScale;

    return getCameraScroll(
      { x: svgCenterX, y: svgCenterY },
      { x: vbX, y: vbY, width: vbW, height: vbH },
      { width: contentW, height: contentH },
      { width: containerW, height: containerH }
    );
  };

  const getLotTooltip = (lot: any) => {
    const lotData = lot.lotId;

    if (!lotData) return "";

    const tooltipPrice = isLandSubdivisionBuyerView.value
      ? lotData.price
      : getLotPackagePrice(lot);

    return `<div class="lot-tooltip">
      <p class="lot-title">LOT ${lotData.title}</p>
      <strong class="lot-status">${getMasterplanLotStatusLabel(
        lotData.status
      ).toUpperCase()}</strong>
      ${lotData.size ? `<p class="lot-size">${lotData.size}m²</p>` : ""}
      ${
        tooltipPrice && !lotData.hidePrice
          ? `<p class="lot-price">$${tooltipPrice.toLocaleString()}</p>`
          : ""
      }
      ${isSoldLot(lot) ? "" : '<p class="lot-action">Click to view details</p>'}
    </div>`;
  };

  const handleLotClick = (lot: any, shouldZoom = false) => {
    if (isSoldLot(lot)) return;

    if (pulseTimeoutId.value !== null) {
      clearTimeout(pulseTimeoutId.value);
      pulseTimeoutId.value = null;
    }

    if (stagePulseTimeoutId.value !== null) {
      clearTimeout(stagePulseTimeoutId.value);
      stagePulseTimeoutId.value = null;
    }

    selectedStageId.value = "";

    selectedLotId.value = lot.lotId?._id || "";

    if (
      shouldZoom &&
      lot.boundingBox &&
      svgContainerMap.value &&
      overlayViewBox.value
    ) {
      const [, , vbW] = overlayViewBox.value.split(" ").map(Number);
      const defaultTargetZoom = Math.min(
        maxZoom.value * 0.8,
        1.6 * (vbW / 1440)
      );
      const targetZoom = clampExpandedMobileClickZoom(
        defaultTargetZoom,
        LOT_ZOOM_THRESHOLD.value,
        "lot"
      );
      const lotCenterX = lot.boundingBox.x + lot.boundingBox.width / 2;
      const lotCenterY = lot.boundingBox.y + lot.boundingBox.height / 2;

      const scrollTarget = computeScrollTarget(
        lotCenterX,
        lotCenterY,
        targetZoom
      );

      if (!scrollTarget) return;

      navigateToTarget(targetZoom, scrollTarget.left, scrollTarget.top);
    }
  };

  const openLotModal = (lot: any) => {
    if (isSoldLot(lot)) return;

    selectedLot.value = lot;
    handleLotClick(lot, false);
    showLotModal.value = true;
  };

  const handleSidebarLotClick = (lot: any) => {
    if (isSoldLot(lot)) return;

    if (isCustomerMobileMapLayout.value) return;

    const lotId = lot.lotId?._id || "";

    if (lotId && selectedLotId.value === lotId) {
      showLotModal.value = true;
      return;
    }

    selectedLot.value = lot;
    handleLotClick(lot, true);
  };

  const viewSidebarLotOnMap = (lot: any) => {
    if (isSoldLot(lot)) return;

    selectedLot.value = lot;
    handleLotClick(lot, true);
    nextTick(() => {
      if (!mapWrapper.value) return;

      const stickyNavbar = document.querySelector(
        ".customer-navbar"
      ) as HTMLElement | null;
      const stickyOffset = props.isEmbedded
        ? 0
        : stickyNavbar?.offsetHeight ?? 0;
      const mapTop =
        mapWrapper.value.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: Math.max(0, mapTop - stickyOffset),
        behavior: "smooth",
      });
    });
  };

  const openSidebarLotDetails = (lot: any) => {
    if (isSoldLot(lot)) return;

    selectedLot.value = lot;
    handleLotClick(lot, false);
    showLotModal.value = true;
  };

  const handlePoiClick = (poi: any) => {
    selectedPoi.value = selectedPoi.value?.title === poi.title ? null : poi;
  };

  const getPoiPinSize = (poi: any) =>
    Math.min(poi.boundingBox.width, poi.boundingBox.height) * 0.75;

  const poiLabelFontSize = computed(() => {
    if (!overlayViewBox.value) return 20;

    const parts = overlayViewBox.value.split(" ").map(Number);

    return Math.round(Math.min(parts[2], parts[3]) / 32);
  });

  const overlayStrokeWidth = computed(() => {
    if (!overlayViewBox.value) return 3.75;

    const [, , vbW] = overlayViewBox.value.split(" ").map(Number);

    return Math.max(2, Math.round(3.75 * (vbW / 1440)));
  });

  const maxZoom = computed(() => {
    const configuredMax = Math.max(
      activeZoomLevels.value?.estateBoundary ?? 1,
      activeZoomLevels.value?.stages ?? 2,
      activeZoomLevels.value?.lots ?? 3
    );

    if (!overlayViewBox.value) return configuredMax;

    const [, , vbW] = overlayViewBox.value.split(" ").map(Number);

    return Math.max(
      configuredMax,
      LOT_ZOOM_THRESHOLD.value + zoomStep.value,
      3,
      Math.ceil((3 * vbW) / 1440)
    );
  });

  const zoomStep = computed(() => {
    if (!overlayViewBox.value) return 0.3;

    const [, , vbW] = overlayViewBox.value.split(" ").map(Number);

    return Math.max(0.3, Math.round(0.3 * (vbW / 1440) * 10) / 10);
  });

  const wheelZoomDelta = computed(() => {
    if (!overlayViewBox.value) return 0.12;

    const [, , vbW] = overlayViewBox.value.split(" ").map(Number);

    return Math.max(0.12, 0.12 * (vbW / 1440));
  });

  const stageZoomThresholdOffset = computed(() => {
    const mobileExpandedMultiplier = shouldCoverMobileMap.value ? 0.65 : 1;

    return 1.5 * wheelZoomDelta.value * mobileExpandedMultiplier;
  });

  const lotZoomThresholdOffset = computed(() => {
    const mobileExpandedMultiplier = shouldCoverMobileMap.value ? 0.55 : 1;

    return 3.5 * wheelZoomDelta.value * mobileExpandedMultiplier;
  });

  const minZoom = computed(() => {
    if (
      !overlayViewBox.value ||
      !containerClientWidth.value ||
      !containerClientHeight.value
    )
      return 1;

    const [, , vbW, vbH] = overlayViewBox.value.split(" ").map(Number);
    const baseWidth = getBaseSvgWidth(
      containerClientWidth.value,
      containerClientHeight.value,
      vbW,
      vbH
    );

    return getMinimumCoverZoom(
      containerClientWidth.value,
      containerClientHeight.value,
      vbW,
      vbH,
      baseWidth
    );
  });

  const compatibleLotIds = computed(
    () =>
      new Set(
        lotsForMatchingDesign.value
          .map((l: any) => l.lotId?._id)
          .filter(Boolean)
      )
  );

  const handleMapLotClick = (lot: any) => {
    if (isSoldLot(lot)) return;

    if (currentSidebarView.value === "lot-matching") {
      const lotId = lot.lotId?._id;

      if (!lotId || !compatibleLotIds.value.has(lotId)) return;

      openLotModal(lot);
      return;
    }

    const lotStageId = lot.lotId?.stageId?._id ?? lot.lotId?.stageId;
    const stage = stages.value.find(
      (s: any) => String(s._id) === String(lotStageId)
    );

    if (stage) {
      selectedStage.value = stage;
      currentSidebarView.value = "lots";
    }

    openLotModal(lot);
  };

  const selectStage = (stage: any) => {
    const stageMeta = masterplanStages.value.find((s: any) => {
      const stageIdValue =
        typeof s.stageId === "object" ? s.stageId._id : s.stageId;

      return String(stageIdValue) === String(stage._id);
    });

    if (stageMeta) {
      handleStageClick(stageMeta, true);
    } else {
      selectedStage.value = stage;
      currentSidebarView.value = "lots";
    }
  };

  const backToStages = () => {
    currentSidebarView.value = "stages";
    selectedStage.value = null;
    selectedLotId.value = "";
    selectedStageId.value = "";
    showPackagesOnly.value = false;

    if (!svgContainerMap.value || !overlayViewBox.value) return;

    if (masterplan.value?.estateBoundaryPath) {
      handleEstateBoundaryClick();
      return;
    }

    const allPaths = masterplanStages.value
      .map((s: any) => s.boundaryPath)
      .filter(Boolean)
      .join(" ");
    const [, , vbW, vbH] = overlayViewBox.value.split(" ").map(Number);
    const container = svgContainerMap.value;
    const baseWidth = getBaseSvgWidth(
      container.clientWidth,
      container.clientHeight,
      vbW,
      vbH
    );
    const meetScale = baseWidth / vbW;

    let centerX = vbW / 2;
    let centerY = vbH / 2;
    const configuredZoom = activeZoomLevels.value?.stages;
    let fitZoom =
      configuredZoom == null
        ? LOT_ZOOM_THRESHOLD.value * 0.95
        : Math.max(minZoom.value, configuredZoom);

    if (allPaths) {
      const bbox = parseStageBbox(allPaths);

      if (bbox) {
        centerX = bbox.centerX;
        centerY = bbox.centerY;

        if (configuredZoom == null) {
          const pxW = bbox.width * meetScale;
          const pxH = bbox.height * meetScale;
          const fitZoomX = (container.clientWidth * 0.7) / pxW;
          const fitZoomY = (container.clientHeight * 0.7) / pxH;

          fitZoom = Math.max(
            STAGE_ZOOM_THRESHOLD.value,
            Math.min(
              LOT_ZOOM_THRESHOLD.value * 0.95,
              Math.min(fitZoomX, fitZoomY)
            )
          );
        }
      }
    }

    const configuredCenter = getDeviceZoomCenter(
      masterplan.value ?? undefined,
      "stageZoomCenter",
      !isDesktop.value,
      isPhoneLandscape.value
    );
    const scrollTarget = computeScrollTarget(
      configuredCenter?.x ?? centerX,
      configuredCenter?.y ?? centerY,
      fitZoom
    );

    if (!scrollTarget) return;

    navigateToTarget(fitZoom, scrollTarget.left, scrollTarget.top);
  };

  const matchingHouseDesignInfo = computed((): any | null => {
    if (!props.matchingHouseDesignId || !masterplan.value?.lots) return null;

    for (const lot of masterplan.value.lots) {
      const allEntries = (lot.lotId?.builders || []).flatMap(
        (b: any) => b.housesIds || []
      );
      const entry = allEntries.find(
        (h: any) =>
          String(h.houseId?._id ?? h.houseId) === props.matchingHouseDesignId
      );

      if (entry && entry.houseId && typeof entry.houseId === "object") {
        return entry.houseId;
      }
    }

    return null;
  });

  const matchingFacadeName = computed((): string | null => {
    if (!props.matchingFacadeId || !matchingHouseDesignInfo.value) return null;

    const facade = (matchingHouseDesignInfo.value.facades || []).find(
      (f: any) => String(f._id) === props.matchingFacadeId
    );

    return facade?.name || null;
  });

  const lotsForMatchingDesign = computed(() => {
    if (!props.matchingHouseDesignId || !masterplan.value?.lots) return [];

    return masterplan.value.lots.filter((lot: any) => {
      const houseEntry = (lot.lotId?.builders || [])
        .flatMap((b: any) => b.housesIds || [])
        .find(
          (h: any) =>
            String(h.houseId?._id ?? h.houseId) === props.matchingHouseDesignId
        );

      if (!houseEntry) return false;

      const status = (lot.lotId?.status || "").toLowerCase();

      if (!availableStatuses.includes(status)) return false;

      if (props.matchingFacadeId) {
        const facadeIds: string[] = (houseEntry?.facadeIds || []).map(String);

        if (facadeIds.length > 0 && !facadeIds.includes(props.matchingFacadeId))
          return false;
      }

      return true;
    });
  });

  const changeDesign = () => {
    if (props.matchingHouseDesignId && estateId.value) {
      router.push({
        name: "HouseInteriorsV2",
        params: { houseId: props.matchingHouseDesignId },
        query: {
          estateId: estateId.value,
          ...(props.matchingFacadeId ? { facade: props.matchingFacadeId } : {}),
          ...(props.isEmbedded ? { embed: "true" } : {}),
        },
      });
    } else {
      router.push({
        name: "BrowseHomeDesignsV2",
        params: { estateId: estateId.value },
        query: props.isEmbedded ? { embed: "true" } : {},
      });
    }
  };

  const goBackFromMatching = () => {
    currentSidebarView.value = "stages";
    selectedLotId.value = "";
    selectedLot.value = null;
    const query = { ...router.currentRoute.value.query };

    delete query.houseDesignId;
    delete query.facadeId;
    router.replace({ query });
  };

  const handleMatchingLotClick = (lot: any) => {
    if (isSoldLot(lot)) return;

    const lotId = lot.lotId?._id || "";

    if (lotId && selectedLotId.value === lotId) {
      selectedLot.value = lot;
      showLotModal.value = true;
      return;
    }

    selectedLot.value = lot;
    handleLotClick(lot, true);
  };

  const viewThresholds = computed(() =>
    getViewThresholds({
      minimumZoom: minZoom.value,
      estateZoom: activeZoomLevels.value?.estateBoundary,
      stageZoom: activeZoomLevels.value?.stages,
      lotZoom: activeZoomLevels.value?.lots,
      hasEstateBoundary: Boolean(masterplan.value?.estateBoundaryPath),
      stageDefaultOffset: stageZoomThresholdOffset.value,
      lotDefaultOffset: lotZoomThresholdOffset.value,
    })
  );
  const STAGE_ZOOM_THRESHOLD = computed(() => viewThresholds.value.stages);
  const LOT_ZOOM_THRESHOLD = computed(() => viewThresholds.value.lots);

  const initialMapZoom = computed(() => {
    const configuredZoom = Math.max(
      minZoom.value,
      activeZoomLevels.value?.estateBoundary ?? minZoom.value
    );

    if (!isCustomerMobileMapLayout.value) return configuredZoom;

    return Math.min(
      maxZoom.value,
      activeZoomLevels.value?.estateBoundary == null
        ? STAGE_ZOOM_THRESHOLD.value + wheelZoomDelta.value
        : configuredZoom
    );
  });

  const expandedMobileClickZoomCap = (target: ClickZoomTarget) => {
    const offsets: Record<ClickZoomTarget, number> = {
      estate: 0.8,
      stage: 0.45,
      lot: 0.75,
    };

    const baseThreshold =
      target === "estate"
        ? STAGE_ZOOM_THRESHOLD.value
        : LOT_ZOOM_THRESHOLD.value;

    return baseThreshold + offsets[target] * wheelZoomDelta.value;
  };

  const clampExpandedMobileClickZoom = (
    targetZoom: number,
    minimumZoom: number,
    target: ClickZoomTarget
  ) => {
    if (!shouldCoverMobileMap.value) {
      return Math.max(minimumZoom, targetZoom);
    }

    return Math.max(
      minimumZoom,
      Math.min(targetZoom, expandedMobileClickZoomCap(target))
    );
  };

  const showLots = computed(() => zoom.value >= LOT_ZOOM_THRESHOLD.value);
  const showStages = computed(() => zoom.value >= STAGE_ZOOM_THRESHOLD.value);

  const zoomCrossesVisibilityThreshold = (
    previousZoom: number,
    nextZoom: number
  ) =>
    [STAGE_ZOOM_THRESHOLD.value, LOT_ZOOM_THRESHOLD.value].some(
      (threshold) =>
        (previousZoom < threshold && nextZoom >= threshold) ||
        (previousZoom >= threshold && nextZoom < threshold)
    );

  const syncZoomForVisibilityThresholds = (nextZoom: number) => {
    if (zoomCrossesVisibilityThreshold(zoom.value, nextZoom)) {
      zoom.value = nextZoom;
    }
  };

  const setZoom = (
    nextZoom: number,
    options: { syncReactive?: boolean; emitZoom?: boolean } = {}
  ) => {
    liveZoom = nextZoom;
    applySvgLayersStyle(nextZoom);

    if (options.syncReactive) {
      zoom.value = nextZoom;
    } else {
      syncZoomForVisibilityThresholds(nextZoom);
    }

    if (options.emitZoom) {
      emit("updateZoom", nextZoom);
    }
  };

  const masterplanStages = computed(() => {
    if (!masterplan.value?.stages?.length) return [];

    return masterplan.value.stages.filter((s: any) => s.boundaryPath);
  });

  const keepVisibilityLayersMounted = computed(() =>
    shouldUseMobilePinchPreview()
  );

  const shouldRenderEstateBoundary = computed(
    () =>
      Boolean(masterplan.value?.estateBoundaryPath) &&
      (keepVisibilityLayersMounted.value || !showStages.value)
  );

  const shouldRenderStageLayer = computed(
    () =>
      masterplanStages.value.length > 0 &&
      (keepVisibilityLayersMounted.value ||
        (showStages.value && !showLots.value))
  );

  const shouldRenderSelectedStageLayer = computed(
    () =>
      keepVisibilityLayersMounted.value ||
      Boolean(showLots.value && selectedStageMeta.value)
  );

  const shouldRenderLotsLayer = computed(
    () => keepVisibilityLayersMounted.value || showLots.value
  );

  const shouldRenderPoiLayer = computed(
    () =>
      Boolean(masterplan.value?.pointsOfInterest?.length) &&
      (keepVisibilityLayersMounted.value || showStages.value)
  );

  const STATUS_CLASS_TO_FILL: Record<string, { var: string; hex: string }> = {
    success: {
      var: "--map-estate-selling-2",
      hex: MASTERPLAN_STATUS_COLORS["success"].bg,
    },
    primary: {
      var: "--map-estate-sold",
      hex: MASTERPLAN_STATUS_COLORS["primary"].bg,
    },
    warning: {
      var: "--map-estate-sold",
      hex: MASTERPLAN_STATUS_COLORS["warning"].bg,
    },
    "light-blue": {
      var: "--map-estate-selling",
      hex: MASTERPLAN_STATUS_COLORS["light-blue"].bg,
    },
    yellow: {
      var: "--map-estate-sold",
      hex: MASTERPLAN_STATUS_COLORS["yellow"].bg,
    },
    gray: {
      var: "--map-estate-not-released",
      hex: MASTERPLAN_STATUS_COLORS["gray"].bg,
    },
  };

  const stageFillFromLots = computed(
    (): Record<string, { var: string; hex: string }> => {
      const map: Record<string, { var: string; hex: string }> = {};
      const lots = masterplan.value?.lots || [];
      const soldOutStatuses: LotStatus[] = [
        LotStatus.EOI,
        LotStatus.CONTRACT_EXECUTED,
        LotStatus.UNCONDITIONAL,
        LotStatus.SETTLED,
        LotStatus.OPTION,
        LotStatus.OFFLINE,
        LotStatus.HOLD,
        LotStatus.PRE_CONTRACT,
        LotStatus.SOLD,
        LotStatus.UNDER_CONTRACT,
      ];

      for (const stageMeta of masterplanStages.value) {
        const stageIdStr = String(
          typeof stageMeta.stageId === "object"
            ? (stageMeta.stageId as any)._id
            : stageMeta.stageId
        );

        const stageLots = lots.filter((lot: any) => {
          const lotStageId = lot.lotId?.stageId?._id ?? lot.lotId?.stageId;

          return String(lotStageId) === stageIdStr;
        });

        if (!stageLots.length) {
          map[stageIdStr] = STATUS_CLASS_TO_FILL["gray"];
          continue;
        }

        const statuses: LotStatus[] = stageLots
          .map((l: any) => l.lotId?.status)
          .filter(Boolean);

        const allNotReleased =
          statuses.length > 0 &&
          statuses.every((s) => s === LotStatus.NOT_RELEASED);

        const hasAvailable = statuses.some((s) =>
          availableStatuses.includes(String(s).toLowerCase())
        );

        const allSoldOut =
          statuses.length > 0 &&
          statuses.every((s) => soldOutStatuses.includes(s));

        if (allNotReleased) {
          map[stageIdStr] = STATUS_CLASS_TO_FILL["gray"];
        } else if (hasAvailable) {
          map[stageIdStr] = STATUS_CLASS_TO_FILL["success"];
        } else if (allSoldOut) {
          map[stageIdStr] = STATUS_CLASS_TO_FILL["warning"];
        } else {
          map[stageIdStr] = STATUS_CLASS_TO_FILL["gray"];
        }
      }

      return map;
    }
  );

  const getStageStatusFill = (stageId: any) => {
    const stageIdStr = String(
      typeof stageId === "object" ? (stageId as any)._id : stageId
    );

    const stageStatus =
      stageStatusMap.value[stageIdStr] ??
      (typeof stageId === "object" ? stageId?.status : null) ??
      stages.value.find((s: any) => String(s._id ?? s) === stageIdStr)?.status;

    if (stageStatus) {
      const cls = getStageStatusClass(stageStatus);

      if (cls && STATUS_CLASS_TO_FILL[cls]) return STATUS_CLASS_TO_FILL[cls];
    }

    return (
      stageFillFromLots.value[stageIdStr] ?? {
        var: "--map-estate-not-released",
        hex: MASTERPLAN_STATUS_COLORS["gray"].bg,
      }
    );
  };

  const getStageGroupStyle = (stageId: any) => {
    const fill = getStageStatusFill(stageId);

    return { "--stage-status-color": fill.hex };
  };

  const getStageFillStyle = (stageId: any) => {
    const fill = getStageStatusFill(stageId);
    const isAvailable = fill.var === "--map-estate-selling-2";

    return {
      fill: `var(${fill.var}, ${fill.hex})`,
      fillOpacity: isAvailable ? "0.4" : "0",
      stroke: "#ffffff",
      strokeOpacity: "1",
      strokeWidth: String(overlayStrokeWidth.value),
    };
  };

  const getStageName = (stageId: any): string => {
    if (!stageId) return "";

    if (typeof stageId === "object" && stageId.title) return stageId.title;

    const idStr = String(
      typeof stageId === "object" ? stageId._id ?? stageId : stageId
    );

    return (
      stages.value.find((s: any) => String(s._id ?? s) === idStr)?.title ?? ""
    );
  };

  const resolvedEstateName = computed(() => {
    if (props.estateName) return props.estateName;

    const eid = masterplan.value?.estateId;

    if (eid && typeof eid === "object" && eid.title) return eid.title;

    return "";
  });

  const estateLabelDisplayText = computed(() => {
    const labelText = masterplan.value?.estateLabelMetadata?.labelText?.trim();

    return labelText || resolvedEstateName.value;
  });

  const estateBoundaryCentroid = computed<{ x: number; y: number } | null>(
    () => {
      if (!masterplan.value?.estateBoundaryPath) return null;

      const tokens = masterplan.value.estateBoundaryPath.trim().split(/\s+/);
      const pts: Array<{ x: number; y: number }> = [];

      for (let i = 0; i < tokens.length; i++) {
        if (tokens[i] === "M" || tokens[i] === "L") {
          const coords = tokens[i + 1]?.split(",");

          if (coords?.length === 2) {
            pts.push({ x: parseFloat(coords[0]), y: parseFloat(coords[1]) });
            i++;
          }
        }
      }

      if (pts.length < 3) return null;

      return {
        x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
        y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
      };
    }
  );

  const estateLabelTransform = computed(() => {
    const c = estateBoundaryCentroid.value;

    if (!c) return "";

    const meta = masterplan.value?.estateLabelMetadata;
    const cx = c.x + (meta?.offsetX || 0);
    const cy = c.y + (meta?.offsetY || 0);
    const scale = meta?.scale ?? 3;
    const rotation = meta?.rotation ?? 0;

    if (scale === 1 && rotation === 0) return "";

    const parts = [`translate(${cx} ${cy})`];

    if (scale !== 1) parts.push(`scale(${scale})`);

    if (rotation !== 0) parts.push(`rotate(${rotation})`);

    parts.push(`translate(${-cx} ${-cy})`);
    return parts.join(" ");
  });

  const estateLabelLogoWidth = computed(() => {
    if (!overlayViewBox.value) return 100;

    const parts = overlayViewBox.value.split(" ").map(Number);

    return Math.round(Math.min(parts[2] || 1000, parts[3] || 1000) / 40) * 25;
  });

  const estateLabelLogoHeight = computed(() =>
    Math.round(estateLabelLogoWidth.value * 0.4)
  );

  const handleEstateBoundaryClick = () => {
    if (
      !masterplan.value?.estateBoundaryPath ||
      !svgContainerMap.value ||
      !overlayViewBox.value
    )
      return;

    const allStagePaths = masterplanStages.value
      .map((stage: any) => stage.boundaryPath)
      .filter(Boolean)
      .join(" ");
    const bbox =
      parseStageBbox(allStagePaths) ||
      parseStageBbox(masterplan.value.estateBoundaryPath);

    if (!bbox) return;

    const [, , vbW, vbH] = overlayViewBox.value.split(" ").map(Number);
    const container = svgContainerMap.value;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const baseWidth = getBaseSvgWidth(containerW, containerH, vbW, vbH);
    const meetScale = baseWidth / vbW;
    const estatePxW = bbox.width * meetScale;
    const estatePxH = bbox.height * meetScale;
    const PAD = 0.7;
    const fitZoomX = (containerW * PAD) / estatePxW;
    const fitZoomY = (containerH * PAD) / estatePxH;
    const configuredZoom = activeZoomLevels.value?.stages;
    const estateMinZoom =
      configuredZoom ??
      (shouldCoverMobileMap.value
        ? STAGE_ZOOM_THRESHOLD.value + 0.35 * wheelZoomDelta.value
        : STAGE_ZOOM_THRESHOLD.value + wheelZoomDelta.value);
    const targetZoom =
      configuredZoom == null
        ? clampExpandedMobileClickZoom(
            Math.max(
              estateMinZoom,
              Math.min(
                LOT_ZOOM_THRESHOLD.value * 0.98,
                Math.min(fitZoomX, fitZoomY)
              )
            ),
            estateMinZoom,
            "estate"
          )
        : STAGE_ZOOM_THRESHOLD.value;
    const configuredCenter = getDeviceZoomCenter(
      masterplan.value ?? undefined,
      "stageZoomCenter",
      !isDesktop.value,
      isPhoneLandscape.value
    );
    const scrollTarget = computeScrollTarget(
      configuredCenter?.x ?? bbox.centerX,
      configuredCenter?.y ?? bbox.centerY,
      targetZoom
    );

    if (!scrollTarget) return;

    navigateToTarget(targetZoom, scrollTarget.left, scrollTarget.top);
  };

  const parseStageBbox = (pathStr: string) => {
    const nums = (pathStr.match(/-?[\d.]+(?:e[+-]?\d+)?/g) ?? []).map(Number);
    const xs: number[] = [];
    const ys: number[] = [];

    for (let i = 0; i + 1 < nums.length; i += 2) {
      xs.push(nums[i]);
      ys.push(nums[i + 1]);
    }

    if (!xs.length) return null;

    const minX = Math.min(...xs),
      maxX = Math.max(...xs);
    const minY = Math.min(...ys),
      maxY = Math.max(...ys);

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
    };
  };

  const getStageCentroid = (stageMeta: any) =>
    getBoundaryLabelCenter(stageMeta.boundaryPath, stageMeta.boundaryPoints);

  const getStageLabelMetadata = (stageMeta: any) => {
    if (!masterplan.value?.stageLabelMetadata) return null;

    const stageIdStr = String(
      typeof stageMeta.stageId === "object"
        ? (stageMeta.stageId as any)._id ?? stageMeta.stageId
        : stageMeta.stageId
    );

    return masterplan.value.stageLabelMetadata.find(
      (m: any) =>
        String(
          typeof m.stageId === "object"
            ? (m.stageId as any)._id ?? m.stageId
            : m.stageId
        ) === stageIdStr
    );
  };

  const getStageLabelCenterX = (stageMeta: any): number => {
    const centroid = getStageCentroid(stageMeta);

    if (!centroid) return 0;

    const meta = getStageLabelMetadata(stageMeta);

    return centroid.x + (meta?.offsetX || 0);
  };

  const getStageLabelCenterY = (stageMeta: any): number => {
    const centroid = getStageCentroid(stageMeta);

    if (!centroid) return 0;

    const meta = getStageLabelMetadata(stageMeta);

    return centroid.y + (meta?.offsetY || 0);
  };

  const getStageLabelTransform = (stageMeta: any): string => {
    const centroid = getStageCentroid(stageMeta);

    if (!centroid) return "";

    const meta = getStageLabelMetadata(stageMeta);

    if (!meta || (meta.scale === 1 && meta.rotation === 0)) return "";

    const x = centroid.x + (meta.offsetX || 0);
    const y = centroid.y + (meta.offsetY || 0);

    const transforms = [];

    transforms.push(`translate(${x}, ${y})`);

    if (meta.scale !== 1) transforms.push(`scale(${meta.scale})`);

    if (meta.rotation !== 0) transforms.push(`rotate(${meta.rotation})`);

    transforms.push(`translate(${-x}, ${-y})`);

    return transforms.join(" ");
  };

  const lotNumberFontSize = computed(() => {
    if (!overlayViewBox.value) return 18;

    const parts = overlayViewBox.value.split(" ").map(Number);
    const baseSize = Math.min(parts[2], parts[3]) / 40;

    return Math.round(baseSize);
  });

  const getStageLabelKey = (stageMeta: any): string =>
    `stage-${String(
      typeof stageMeta.stageId === "object"
        ? stageMeta.stageId._id ?? stageMeta.stageId
        : stageMeta.stageId
    )}`;

  const labelTextRefs = ref<Record<string, SVGTextElement>>({});
  const labelBboxes = ref<
    Record<string, { x: number; y: number; width: number; height: number }>
  >({});

  const getLabelRectAttrs = (key: string) => {
    const bb = labelBboxes.value[key];

    if (!bb) return null;

    const fs = lotNumberFontSize.value * 2;
    const padX = fs * 0.1;
    const padY = fs * 0.05;
    const rx = fs * 0.08;

    return {
      x: bb.x - padX,
      y: bb.y - padY,
      width: bb.width + padX * 2,
      height: bb.height + padY * 2,
      rx,
    };
  };

  const measureLabelBboxes = () => {
    nextTick(() => {
      requestAnimationFrame(() => {
        const result: Record<
          string,
          { x: number; y: number; width: number; height: number }
        > = {};

        for (const [key, el] of Object.entries(labelTextRefs.value)) {
          if (el && (el as SVGTextElement).isConnected) {
            const bb = (el as SVGTextElement).getBBox();

            if (bb.width > 0)
              result[key] = {
                x: bb.x,
                y: bb.y,
                width: bb.width,
                height: bb.height,
              };
          }
        }

        labelBboxes.value = result;
      });
    });
  };

  const getLotNumberCenter = (lot: any): { x: number; y: number } | null => {
    if (!lot) return null;

    if (lot.polygonPoints && lot.polygonPoints.length > 0) {
      return calculatePolygonCentroid(lot.polygonPoints);
    } else if (lot.boundingBox) {
      return calculateBoundingBoxCenter(lot.boundingBox);
    }

    return null;
  };

  const getLotNumberMetadata = (lot: any) => {
    if (!masterplan.value?.lotNumberMetadata) return null;

    return masterplan.value.lotNumberMetadata.find(
      (m: any) => String(m.lotId) === String(lot.lotId?._id ?? lot.lotId)
    );
  };

  const getLotSizeMetadata = (lot: any) => {
    if (!masterplan.value?.lotSizeMetadata) return null;

    return masterplan.value.lotSizeMetadata.find(
      (m: any) => String(m.lotId) === String(lot.lotId?._id ?? lot.lotId)
    );
  };

  const shouldShowStageLabel = (stageMeta: any): boolean => {
    const metadata = getStageLabelMetadata(stageMeta);

    if (!metadata) return true;

    return metadata.displayed !== false;
  };

  const shouldShowLotNumber = (lot: any): boolean => {
    if (!lot.lotId) return false;

    const metadata = getLotNumberMetadata(lot);

    if (metadata?.displayed === false) return false;

    return isLotLabelStageVisible(lot);
  };

  const isLotLabelStageVisible = (lot: any): boolean => {
    const lotStageId = lot.stageId
      ? String(
          typeof lot.stageId === "object"
            ? lot.stageId._id ?? lot.stageId
            : lot.stageId
        )
      : String(lot.lotId?.stageId?._id ?? lot.lotId?.stageId ?? "");

    if (!lotStageId) return false;

    const stageFill = stageFillFromLots.value[lotStageId];

    return stageFill?.var === "--map-estate-selling-2";
  };

  const shouldShowLotSize = (lot: any): boolean => {
    if (!lot.lotId || !isLotLabelStageVisible(lot)) return false;

    if (lot.lotId?.size === null || lot.lotId?.size === undefined) return false;

    return getLotSizeMetadata(lot)?.displayed !== false;
  };

  const formatLotSize = (size: number | string): string => `${size}m²`;

  const getLotNumberCenterX = (lot: any): number => {
    const center = getLotNumberCenter(lot);

    if (!center) return 0;

    const metadata = getLotNumberMetadata(lot);

    return center.x + (metadata?.offsetX || 0);
  };

  const getLotNumberCenterY = (lot: any): number => {
    const center = getLotNumberCenter(lot);

    if (!center) return 0;

    const metadata = getLotNumberMetadata(lot);

    return center.y + (metadata?.offsetY || 0);
  };

  const getLotNumberTransform = (lot: any): string => {
    const center = getLotNumberCenter(lot);

    if (!center) return "";

    const metadata = getLotNumberMetadata(lot);

    if (!metadata || (metadata.scale === 1 && metadata.rotation === 0))
      return "";

    const x = center.x + (metadata.offsetX || 0);
    const y = center.y + (metadata.offsetY || 0);
    const transforms = [];

    transforms.push(`translate(${x}, ${y})`);

    if (metadata.scale !== 1) transforms.push(`scale(${metadata.scale})`);

    if (metadata.rotation !== 0)
      transforms.push(`rotate(${metadata.rotation})`);

    transforms.push(`translate(${-x}, ${-y})`);

    return transforms.join(" ");
  };

  const getLotSizeCenterX = (lot: any): number => {
    const center = getLotNumberCenter(lot);

    if (!center) return 0;

    const numberMetadata = getLotNumberMetadata(lot);
    const sizeMetadata = getLotSizeMetadata(lot);

    return (
      center.x + (numberMetadata?.offsetX || 0) + (sizeMetadata?.offsetX ?? 2)
    );
  };

  const getLotSizeCenterY = (lot: any): number => {
    const center = getLotNumberCenter(lot);

    if (!center) return 0;

    const numberMetadata = getLotNumberMetadata(lot);
    const sizeMetadata = getLotSizeMetadata(lot);
    const numberScale = numberMetadata?.scale ?? 1;
    const sizeScale = sizeMetadata?.scale ?? LOT_SIZE_DEFAULT_SCALE;
    const labelGap = getLotLabelGap(numberScale, sizeScale);

    return (
      center.y +
      (numberMetadata?.offsetY || 0) +
      labelGap +
      (sizeMetadata?.offsetY ?? 2)
    );
  };

  const getLotSizeTransform = (lot: any): string => {
    const metadata = getLotSizeMetadata(lot);
    const scale = metadata?.scale ?? LOT_SIZE_DEFAULT_SCALE;
    const rotation = metadata?.rotation ?? 0;

    if (scale === 1 && rotation === 0) return "";

    const x = getLotSizeCenterX(lot);
    const y = getLotSizeCenterY(lot);
    const transforms = [`translate(${x}, ${y})`];

    if (scale !== 1) transforms.push(`scale(${scale})`);

    if (rotation !== 0) transforms.push(`rotate(${rotation})`);

    transforms.push(`translate(${-x}, ${-y})`);
    return transforms.join(" ");
  };

  const handleStageClick = (stageMeta: any, showPulse = false) => {
    if (
      !stageMeta.boundaryPath ||
      !svgContainerMap.value ||
      !overlayViewBox.value
    )
      return;

    const bbox = parseStageBbox(stageMeta.boundaryPath);

    if (!bbox) return;

    const stageIdValue =
      typeof stageMeta.stageId === "object"
        ? stageMeta.stageId._id
        : stageMeta.stageId;

    if (showPulse) {
      selectedStageId.value = stageIdValue;

      if (stagePulseTimeoutId.value !== null) {
        clearTimeout(stagePulseTimeoutId.value);
      }

      stagePulseTimeoutId.value = window.setTimeout(() => {
        selectedStageId.value = "";
      }, 3000);
    } else {
      if (stagePulseTimeoutId.value !== null) {
        clearTimeout(stagePulseTimeoutId.value);
        stagePulseTimeoutId.value = null;
      }

      selectedStageId.value = "";
    }

    const [, , vbWidth, vbHeight] = overlayViewBox.value.split(" ").map(Number);
    const container = svgContainerMap.value;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const baseWidth = getBaseSvgWidth(
      containerWidth,
      containerHeight,
      vbWidth,
      vbHeight
    );
    const meetScale = baseWidth / vbWidth;
    const stagePxW = bbox.width * meetScale;
    const stagePxH = bbox.height * meetScale;

    let targetZoom: number;
    const configuredStageZoom = activeZoomLevels.value?.lots;

    if (configuredStageZoom != null) {
      targetZoom = LOT_ZOOM_THRESHOLD.value;
    } else if (showPulse) {
      const PAD = 0.65;
      const fitZoomX = (containerWidth * PAD) / stagePxW;
      const fitZoomY = (containerHeight * PAD) / stagePxH;
      const stageMaxZ = Math.max(2.5, 2.5 * (vbWidth / 1440));

      targetZoom = clampExpandedMobileClickZoom(
        Math.max(
          LOT_ZOOM_THRESHOLD.value,
          Math.min(stageMaxZ, Math.min(fitZoomX, fitZoomY))
        ),
        LOT_ZOOM_THRESHOLD.value,
        "stage"
      );
    } else {
      const PAD = 0.5;
      const fitZoomX = (containerWidth * PAD) / stagePxW;
      const fitZoomY = (containerHeight * PAD) / stagePxH;
      const stageMaxZ = Math.max(2, 2 * (vbWidth / 1440));

      targetZoom = clampExpandedMobileClickZoom(
        Math.max(
          LOT_ZOOM_THRESHOLD.value,
          Math.min(stageMaxZ, Math.min(fitZoomX, fitZoomY))
        ),
        LOT_ZOOM_THRESHOLD.value,
        "stage"
      );
    }

    const scrollTarget = computeScrollTarget(
      bbox.centerX,
      bbox.centerY,
      targetZoom
    );

    if (!scrollTarget) return;

    navigateToTarget(targetZoom, scrollTarget.left, scrollTarget.top);

    const stage = stagesWithCounts.value.find(
      (s: any) => String(s._id) === String(stageIdValue)
    );

    if (stage) {
      selectedStage.value = stage;
      currentSidebarView.value = "lots";
    } else {
      const rawStage = stages.value.find(
        (s: any) => String(s._id) === String(stageIdValue)
      );

      if (rawStage) {
        selectedStage.value = { ...rawStage, totalLots: 0, availableLots: 0 };
        currentSidebarView.value = "lots";
      }
    }
  };

  const extractViewBox = () => {
    if (masterplan.value?.coordinateViewBox) {
      overlayViewBox.value = masterplan.value.coordinateViewBox;
      const [, , width, height] = masterplan.value.coordinateViewBox
        .split(" ")
        .map(Number);

      return;
    }

    const vbMatch = svgContent.value.match(
      /<svg[^>]*\bviewBox=["']([^"']+)["']/
    );

    if (vbMatch) {
      overlayViewBox.value = vbMatch[1];
    } else {
      const wMatch = svgContent.value.match(
        /<svg[^>]*\bwidth=["']([^"']+)["']/
      );
      const hMatch = svgContent.value.match(
        /<svg[^>]*\bheight=["']([^"']+)["']/
      );
      const width = parseFloat(wMatch?.[1] || "1920");
      const height = parseFloat(hMatch?.[1] || "1080");

      overlayViewBox.value = `0 0 ${width} ${height}`;
    }
  };

  const centerScrollPosition = () => {
    if (!svgContainerMap.value) return;

    const container = svgContainerMap.value;
    const configuredCenter = getDeviceZoomCenter(
      masterplan.value ?? undefined,
      "estateZoomCenter",
      !isDesktop.value,
      isPhoneLandscape.value
    );

    if (configuredCenter) {
      const scrollTarget = computeScrollTarget(
        configuredCenter.x,
        configuredCenter.y,
        liveZoom
      );

      if (scrollTarget) {
        container.scrollLeft = scrollTarget.left;
        container.scrollTop = scrollTarget.top;
        return;
      }
    }

    const scrollWidth = container.scrollWidth;
    const scrollHeight = container.scrollHeight;
    const clientWidth = container.clientWidth;
    const clientHeight = container.clientHeight;

    container.scrollLeft = (scrollWidth - clientWidth) / 2;
    container.scrollTop = (scrollHeight - clientHeight) / 2;
  };

  const zoomToCompatibleLots = () => {
    if (!svgContainerMap.value || !overlayViewBox.value) return;

    const [vbX, vbY, vbW, vbH] = overlayViewBox.value.split(" ").map(Number);
    const centerX = vbX + vbW / 2;
    const centerY = vbY + vbH / 2;
    const targetZoom = LOT_ZOOM_THRESHOLD.value;

    const scrollTarget = computeScrollTarget(centerX, centerY, targetZoom);

    if (!scrollTarget) return;

    navigateToTarget(targetZoom, scrollTarget.left, scrollTarget.top, 800);
  };

  const zoomIn = () => {
    const targetZ = Math.min(liveZoom + zoomStep.value, maxZoom.value);

    if (!svgContainerMap.value) return;

    const container = svgContainerMap.value;
    const fX = container.clientWidth / 2;
    const fY = container.clientHeight / 2;
    const contentFocalX = container.scrollLeft + fX;
    const contentFocalY = container.scrollTop + fY;
    const targetScrollLeft = contentFocalX * (targetZ / liveZoom) - fX;
    const targetScrollTop = contentFocalY * (targetZ / liveZoom) - fY;

    wheelZoomTarget.value = targetZ;
    navigateToTarget(
      targetZ,
      Math.max(0, targetScrollLeft),
      Math.max(0, targetScrollTop),
      300
    );
  };

  const zoomOut = () => {
    const targetZ = Math.max(liveZoom - zoomStep.value, minZoom.value);

    if (!svgContainerMap.value) return;

    const container = svgContainerMap.value;
    const fX = container.clientWidth / 2;
    const fY = container.clientHeight / 2;
    const contentFocalX = container.scrollLeft + fX;
    const contentFocalY = container.scrollTop + fY;
    const targetScrollLeft = contentFocalX * (targetZ / liveZoom) - fX;
    const targetScrollTop = contentFocalY * (targetZ / liveZoom) - fY;

    wheelZoomTarget.value = targetZ;
    navigateToTarget(
      targetZ,
      Math.max(0, targetScrollLeft),
      Math.max(0, targetScrollTop),
      300
    );
  };

  const refreshMapContainerSize = () => {
    if (!svgContainerMap.value) return;

    containerClientWidth.value = svgContainerMap.value.clientWidth;
    containerClientHeight.value = svgContainerMap.value.clientHeight;
    applySvgLayersStyle();
  };

  const refreshViewportSize = () => {
    viewportWidth.value = window.innerWidth;
    viewportHeight.value = window.innerHeight;
  };

  const clampZoomToContainer = (recenter = false) => {
    refreshMapContainerSize();
    const nextMinZoom = minZoom.value;

    if (liveZoom < nextMinZoom) {
      setZoom(nextMinZoom, { syncReactive: true, emitZoom: true });
      wheelZoomTarget.value = nextMinZoom;
    }

    if (recenter) {
      requestAnimationFrame(() => {
        centerScrollPosition();
      });
    }
  };

  const toggleMobileMap = () => {
    mobileMapExpanded.value = !mobileMapExpanded.value;
    emit("mobile-map-expanded-change", mobileMapExpanded.value);
    nextTick(() => {
      requestAnimationFrame(() => {
        clampZoomToContainer(true);
      });
    });
  };

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();

    if (!svgContainerMap.value) return;

    const container = svgContainerMap.value;
    const rect = container.getBoundingClientRect();
    const fX = event.clientX - rect.left;
    const fY = event.clientY - rect.top;

    const delta =
      event.deltaY > 0 ? -wheelZoomDelta.value : wheelZoomDelta.value;

    wheelZoomTarget.value = Math.max(
      minZoom.value,
      Math.min(maxZoom.value, wheelZoomTarget.value + delta)
    );
    wheelZoomFocalX.value = fX;
    wheelZoomFocalY.value = fY;

    if (navAnimFrame.value !== null) {
      cancelAnimationFrame(navAnimFrame.value);
      navAnimFrame.value = null;
    }

    if (wheelAnimFrame.value === null) {
      const animate = () => {
        const cz = liveZoom;
        const diff = wheelZoomTarget.value - cz;

        if (Math.abs(diff) < 0.0015) {
          setZoom(wheelZoomTarget.value, {
            syncReactive: true,
            emitZoom: true,
          });
          wheelAnimFrame.value = null;
          return;
        }

        const newZoom = cz + diff * 0.18;
        const ratio = newZoom / cz;

        const focalX = wheelZoomFocalX.value;
        const focalY = wheelZoomFocalY.value;
        const contentFocalX = (container.scrollLeft + focalX) / cz;
        const contentFocalY = (container.scrollTop + focalY) / cz;
        const crossedVisibilityThreshold = zoomCrossesVisibilityThreshold(
          zoom.value,
          newZoom
        );

        container.scrollLeft = (container.scrollLeft + focalX) * ratio - focalX;
        container.scrollTop = (container.scrollTop + focalY) * ratio - focalY;

        setZoom(newZoom);

        if (crossedVisibilityThreshold) {
          restoreScrollForFocalAfterPatch(
            container,
            contentFocalX,
            contentFocalY,
            () => liveZoom,
            focalX,
            focalY
          );
        }

        wheelAnimFrame.value = requestAnimationFrame(animate);
      };

      wheelAnimFrame.value = requestAnimationFrame(animate);
    }
  };

  const startDrag = (event: MouseEvent) => {
    wasDragging.value = false;
    startDragging(
      event,
      liveZoom,
      isDragging,
      svgContainerMap,
      svgStartPosition,
      svgStartScrollPosition
    );
  };

  const handleDrag = (event: MouseEvent) => {
    if (isDragging.value) {
      const dx = event.clientX - svgStartPosition.value.x;
      const dy = event.clientY - svgStartPosition.value.y;

      if (Math.sqrt(dx * dx + dy * dy) > 6) {
        wasDragging.value = true;
      }
    }

    handleDragging(
      event,
      isDragging,
      svgContainerMap,
      svgStartPosition,
      svgStartScrollPosition
    );
  };

  const stopDrag = () => {
    stopDragging(liveZoom, isDragging, svgContainerMap);
  };

  const suppressClickAfterDrag = (event: MouseEvent) => {
    if (wasDragging.value) {
      event.stopPropagation();
      wasDragging.value = false;
    }
  };

  const getHammerFocalPoint = (event: HammerInput, rect: DOMRect) => {
    return {
      x: event.center.x - rect.left,
      y: event.center.y - rect.top,
    };
  };

  const startPinchZoom = (event: HammerInput) => {
    if (!svgContainerMap.value) return;

    const container = svgContainerMap.value;

    pinchContainerRect.value = container.getBoundingClientRect();
    const focal = getHammerFocalPoint(event, pinchContainerRect.value);

    if (navAnimFrame.value !== null) {
      cancelAnimationFrame(navAnimFrame.value);
      navAnimFrame.value = null;
    }

    if (wheelAnimFrame.value !== null) {
      cancelAnimationFrame(wheelAnimFrame.value);
      wheelAnimFrame.value = null;
    }

    isPinching.value = true;
    wasDragging.value = true;
    pinchStartZoom.value = liveZoom;
    pinchStartScrollX.value = container.scrollLeft;
    pinchStartScrollY.value = container.scrollTop;
    pinchContentFocalX.value = (container.scrollLeft + focal.x) / liveZoom;
    pinchContentFocalY.value = (container.scrollTop + focal.y) / liveZoom;
    pendingPinchZoom = liveZoom;
    pendingPinchFocalX = focal.x;
    pendingPinchFocalY = focal.y;

    if (shouldUseMobilePinchPreview() && svgLayers.value) {
      svgLayers.value.classList.add("mobile-pinch-zooming");
      svgLayers.value.style.transformOrigin = "0 0";
    }
  };

  const handlePinchZoom = (event: HammerInput) => {
    if (!svgContainerMap.value) return;

    if (!isPinching.value) startPinchZoom(event);

    const container = svgContainerMap.value;
    const rect = pinchContainerRect.value ?? container.getBoundingClientRect();

    pinchContainerRect.value = rect;
    const focal = getHammerFocalPoint(event, rect);
    const targetZoom = Math.max(
      minZoom.value,
      Math.min(maxZoom.value, pinchStartZoom.value * event.scale)
    );

    if (shouldUseMobilePinchPreview()) {
      scheduleMobilePinchPreview(targetZoom, focal.x, focal.y);
      return;
    }

    setZoom(targetZoom);
    wheelZoomTarget.value = targetZoom;
    container.scrollLeft = pinchContentFocalX.value * targetZoom - focal.x;
    container.scrollTop = pinchContentFocalY.value * targetZoom - focal.y;
  };

  const stopPinchZoom = () => {
    if (pinchAnimFrame !== null) {
      cancelAnimationFrame(pinchAnimFrame);
      pinchAnimFrame = null;
      applyMobilePinchPreview(
        pendingPinchZoom,
        pendingPinchFocalX,
        pendingPinchFocalY
      );
    }

    if (shouldUseMobilePinchPreview() && svgContainerMap.value) {
      const container = svgContainerMap.value;
      const finalZoom = liveZoom;

      resetSvgLayersTransform();
      setZoom(finalZoom, { syncReactive: true, emitZoom: true });
      restoreScrollForFocal(
        container,
        pinchContentFocalX.value,
        pinchContentFocalY.value,
        finalZoom,
        pendingPinchFocalX,
        pendingPinchFocalY
      );
      restoreScrollForFocalAfterPatch(
        container,
        pinchContentFocalX.value,
        pinchContentFocalY.value,
        () => finalZoom,
        pendingPinchFocalX,
        pendingPinchFocalY
      );
    } else {
      setZoom(liveZoom, { syncReactive: true, emitZoom: true });
    }

    isPinching.value = false;
    pinchContainerRect.value = null;
  };

  const getMasterplan = async () => {
    lotsLoading.value = true;

    try {
      if (props.initialMasterplan != null) {
        masterplan.value =
          props.initialMasterplan as InteractiveMasterplanInterface;
      } else {
        const res = await store.dispatch(
          "interactiveMasterplan/getMasterplanByEstateId",
          estateId.value
        );

        masterplan.value = res.data;
      }

      const svgUrl = masterplan.value?.svgFile?.url;
      let svgText = "";

      if (svgUrl) {
        const cacheKey = `mp_svg_${svgUrl}`;
        const cached = sessionStorage.getItem(cacheKey);

        if (cached) {
          svgText = cached;
        } else {
          svgText = await fetch(svgUrl).then((r) => r.text());

          try {
            sessionStorage.setItem(cacheKey, svgText);
          } catch (_) {
            /* quota exceeded */
          }
        }
      }

      fetchStages();

      if (svgText) {
        svgContent.value = svgText;
        backfillStatusBarPaths(svgText);
        extractViewBox();

        if (svgContainerMap.value) {
          containerClientWidth.value = svgContainerMap.value.clientWidth;
          containerClientHeight.value = svgContainerMap.value.clientHeight;
        }

        const startingZoom = initialMapZoom.value;

        if (startingZoom !== liveZoom) {
          setZoom(startingZoom, { syncReactive: true, emitZoom: true });
          wheelZoomTarget.value = startingZoom;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (props.matchingHouseDesignId) {
              zoomToCompatibleLots();
            } else {
              centerScrollPosition();
            }
          });
        });
      }
    } catch (error) {
      if (
        (error as { response?: { status?: number } }).response?.status === 404
      ) {
        masterplan.value = null;
      }
    } finally {
      lotsLoading.value = false;
      emit("map-ready");
    }
  };

  const fetchStages = () => {
    const masterplanStageEntries = masterplan.value?.stages || [];

    stages.value = masterplanStageEntries
      .map((ms: any) => ms.stageId)
      .filter(Boolean);

    if (estateId.value) {
      store
        .dispatch("stage/getStagesMap", { estateId: estateId.value })
        .then((res: any) => {
          const map: Record<string, string> = {};

          for (const entry of res.data || []) {
            const id = String(entry.stageId?._id ?? entry.stageId ?? "");
            const status = entry.stageId?.status;

            if (id && status) map[id] = status;
          }

          stageStatusMap.value = map;
        })
        .catch(() => {
          /* non-critical */
        });
    }
  };

  const toCreateMasterplan = () => {
    router.push({
      name: "AdminInteractiveMasterplanCreate",
      params: { userId, estateId: estateId.value },
    });
  };

  const toEditMasterplan = () => {
    router.push({
      name: "AdminInteractiveMasterplanEdit",
      params: { userId, estateId: estateId.value, id: masterplan.value._id },
    });
  };

  const deleteMasterplan = () => {
    store
      .dispatch("interactiveMasterplan/deleteMasterplan", masterplan.value._id)
      .then(() => {
        masterplan.value = null;
        svgContent.value = "";
        showConfirmDeleteModal.value = false;
      });
  };

  onMounted(() => {
    refreshViewportSize();
    window.addEventListener("resize", refreshViewportSize);
    window.addEventListener("orientationchange", refreshViewportSize);
    getMasterplan();

    if (svgContainerMap.value) {
      resizeObserver.value = new ResizeObserver(() => {
        clampZoomToContainer(true);
      });
      resizeObserver.value.observe(svgContainerMap.value);

      const mc = new Hammer(svgContainerMap.value);

      hammerManager.value = mc;
      mc.get("pinch").set({ enable: true });
      mc.on("pinchstart", startPinchZoom);
      mc.on("pinch", handlePinchZoom);
      mc.on("pinchend pinchcancel", stopPinchZoom);

      mc.on("panstart", (event: HammerInput) => {
        if (event.pointers.length > 1 || isPinching.value) return;

        svgStartPosition.value = { x: event.center.x, y: event.center.y };
        svgStartScrollPosition.value = {
          x: svgContainerMap.value.scrollLeft,
          y: svgContainerMap.value.scrollTop,
        };
      });

      mc.on("pan", (event: HammerInput) => {
        if (event.pointers.length > 1 || isPinching.value) return;

        const deltaX = event.center.x - svgStartPosition.value.x;
        const deltaY = event.center.y - svgStartPosition.value.y;

        svgContainerMap.value.scrollLeft =
          svgStartScrollPosition.value.x - deltaX;
        svgContainerMap.value.scrollTop =
          svgStartScrollPosition.value.y - deltaY;
      });
    }
  });

  watch(masterplan, () => {
    measureLabelBboxes();
  });
  watch(containerClientWidth, (val, oldVal) => {
    measureLabelBboxes();

    if (oldVal === 0 && val > 0) {
      setTimeout(() => measureLabelBboxes(), 200);
    }
  });
  watch(
    () => [showLots.value, showStages.value],
    () => {
      measureLabelBboxes();
    }
  );
  watch(estateBoundaryCentroid, (val) => {
    if (val) measureLabelBboxes();
  });

  watch(isPhoneLandscape, () => {
    if (!isCustomerMobileMapLayout.value || !svgContainerMap.value) return;

    const orientationZoom = initialMapZoom.value;

    setZoom(orientationZoom, { syncReactive: true, emitZoom: true });
    wheelZoomTarget.value = orientationZoom;

    nextTick(() => requestAnimationFrame(centerScrollPosition));
  });

  onUnmounted(() => {
    clearLotPreview();

    if (mobileMapExpanded.value) {
      emit("mobile-map-expanded-change", false);
    }

    window.removeEventListener("resize", refreshViewportSize);
    window.removeEventListener("orientationchange", refreshViewportSize);

    if (pulseTimeoutId.value !== null) {
      clearTimeout(pulseTimeoutId.value);
    }

    if (stagePulseTimeoutId.value !== null) {
      clearTimeout(stagePulseTimeoutId.value);
    }

    if (wheelAnimFrame.value !== null) {
      cancelAnimationFrame(wheelAnimFrame.value);
    }

    if (navAnimFrame.value !== null) {
      cancelAnimationFrame(navAnimFrame.value);
    }

    if (pinchAnimFrame !== null) {
      cancelAnimationFrame(pinchAnimFrame);
      pinchAnimFrame = null;
    }

    if (hammerManager.value) {
      hammerManager.value.destroy();
    }

    if (resizeObserver.value) {
      resizeObserver.value.disconnect();
    }
  });

  watch(
    () => props.estateId,
    () => {
      getMasterplan();
    }
  );

  watch(
    () => props.initialMasterplan,
    () => {
      getMasterplan();
    }
  );

  watch(showLots, (visible) => {
    if (!visible) {
      if (currentSidebarView.value !== "lot-matching") {
        selectedLotId.value = "";
      }

      if (currentSidebarView.value === "lots") {
        currentSidebarView.value = "stages";
        selectedStage.value = null;
        selectedStageId.value = "";
      }
    }
  });

  watch(
    () => props.matchingHouseDesignId,
    (id) => {
      if (id) currentSidebarView.value = "lot-matching";
    },
    { immediate: true }
  );

const __returned__ = { emit, store, mobileLayoutWidth, isDesktop, userId, estateId, masterplan, svgContent, overlayViewBox, zoom, containerClientWidth, containerClientHeight, resizeObserver, isDragging, wasDragging, mapWrapper, svgContainerMap, svgLayers, svgStartPosition, svgStartScrollPosition, showConfirmDeleteModal, showLotModal, lotsLoading, hoverLotId, selectedLotId, selectedStageId, pulseTimeoutId, stagePulseTimeoutId, currentSidebarView, hammerManager, isPinching, pinchStartZoom, pinchStartScrollX, pinchStartScrollY, pinchContentFocalX, pinchContentFocalY, pinchContainerRect, mobileMapExpanded, viewportWidth, viewportHeight, masterplanCssVars, isPhoneLandscape, activeZoomLevels, wheelZoomTarget, wheelZoomFocalX, wheelZoomFocalY, wheelAnimFrame, navAnimFrame, get liveZoom() { return liveZoom }, set liveZoom(v) { liveZoom = v }, get pinchAnimFrame() { return pinchAnimFrame }, set pinchAnimFrame(v) { pinchAnimFrame = v }, get pendingPinchZoom() { return pendingPinchZoom }, set pendingPinchZoom(v) { pendingPinchZoom = v }, get pendingPinchFocalX() { return pendingPinchFocalX }, set pendingPinchFocalX(v) { pendingPinchFocalX = v }, get pendingPinchFocalY() { return pendingPinchFocalY }, set pendingPinchFocalY(v) { pendingPinchFocalY = v }, stages, stageStatusMap, selectedStage, selectedLot, selectedPoi, showPackagesOnly, lotPreviewUrl, props, isLandSubdivisionBuyerView, clearLotPreview, getPreviewLotBounds, copyRenderedSvgStyles, createLotPreview, hasLandSubdivisionPackagesFeature, isCustomerMobileMapLayout, shouldCoverMobileMap, getBaseSvgWidth, onSelectLot, getEstateId, getSvgLayersStyle, applySvgLayersStyle, shouldUseMobilePinchPreview, resetSvgLayersTransform, applyMobilePinchPreview, restoreScrollForFocal, restoreScrollForFocalAfterPatch, scheduleMobilePinchPreview, availableStatuses, stagesWithCounts, lotsForSelectedStage, selectedStageLotsCount, selectedStageAvailableCount, availableLotsForSelectedStage, displayedLandSubdivisionLots, selectedStageMeta, processedSvgForBackground, backfillStatusBarPaths, getLotPolygonPoints, soldLotStatuses, isSoldLot, getLotClasses, isStageNowSelling, getStageClasses, getLotHouseDesignEntries, getLotPackageCount, getLotPackageAvailabilityLabel, getLandSubdivisionLotPrice, getHouseDesignId, getUniqueLotHouseDesignEntries, getLotHouseDesign, getLotDesignLabel, countLotFacadeOptions, getLotAssignedFacade, getLotAssignedFacadeName, getLotFacadeLabel, getLotAssignedFacadeUrl, getLotPackagePrice, formatPrice, onLotHover, easeInOutCubic, navigateToTarget, computeScrollTarget, getLotTooltip, handleLotClick, openLotModal, handleSidebarLotClick, viewSidebarLotOnMap, openSidebarLotDetails, handlePoiClick, getPoiPinSize, poiLabelFontSize, overlayStrokeWidth, maxZoom, zoomStep, wheelZoomDelta, stageZoomThresholdOffset, lotZoomThresholdOffset, minZoom, compatibleLotIds, handleMapLotClick, selectStage, backToStages, matchingHouseDesignInfo, matchingFacadeName, lotsForMatchingDesign, changeDesign, goBackFromMatching, handleMatchingLotClick, viewThresholds, STAGE_ZOOM_THRESHOLD, LOT_ZOOM_THRESHOLD, initialMapZoom, expandedMobileClickZoomCap, clampExpandedMobileClickZoom, showLots, showStages, zoomCrossesVisibilityThreshold, syncZoomForVisibilityThresholds, setZoom, masterplanStages, keepVisibilityLayersMounted, shouldRenderEstateBoundary, shouldRenderStageLayer, shouldRenderSelectedStageLayer, shouldRenderLotsLayer, shouldRenderPoiLayer, STATUS_CLASS_TO_FILL, stageFillFromLots, getStageStatusFill, getStageGroupStyle, getStageFillStyle, getStageName, resolvedEstateName, estateLabelDisplayText, estateBoundaryCentroid, estateLabelTransform, estateLabelLogoWidth, estateLabelLogoHeight, handleEstateBoundaryClick, parseStageBbox, getStageCentroid, getStageLabelMetadata, getStageLabelCenterX, getStageLabelCenterY, getStageLabelTransform, lotNumberFontSize, getStageLabelKey, labelTextRefs, labelBboxes, getLabelRectAttrs, measureLabelBboxes, getLotNumberCenter, getLotNumberMetadata, getLotSizeMetadata, shouldShowStageLabel, shouldShowLotNumber, isLotLabelStageVisible, shouldShowLotSize, formatLotSize, getLotNumberCenterX, getLotNumberCenterY, getLotNumberTransform, getLotSizeCenterX, getLotSizeCenterY, getLotSizeTransform, handleStageClick, extractViewBox, centerScrollPosition, zoomToCompatibleLots, zoomIn, zoomOut, refreshMapContainerSize, refreshViewportSize, clampZoomToContainer, toggleMobileMap, handleWheel, startDrag, handleDrag, stopDrag, suppressClickAfterDrag, getHammerFocalPoint, startPinchZoom, handlePinchZoom, stopPinchZoom, getMasterplan, fetchStages, toCreateMasterplan, toEditMasterplan, deleteMasterplan, ConfirmDeleteModal, LandSubdivisionLotDetailModal, LotDetailModal, get router() { return router }, computed, nextTick, onMounted, onUnmounted, ref, watch, get useStore() { return useStore }, get Bed() { return Bed }, get Bath() { return Bath }, get Car() { return Car }, get Expand() { return Expand }, get Maximize2() { return Maximize2 }, get MoveHorizontal() { return MoveHorizontal }, get Home() { return Home }, get ChevronRight() { return ChevronRight }, get Info() { return Info }, get MapIcon() { return MapIcon }, get UserRoles() { return UserRoles }, get useIsDesktop() { return useIsDesktop }, get Hammer() { return Hammer }, get handleDragging() { return handleDragging }, get startDragging() { return startDragging }, get stopDragging() { return stopDragging }, get getDeviceZoomCenter() { return getDeviceZoomCenter }, get getCameraScroll() { return getCameraScroll }, get getDeviceZoomLevels() { return getDeviceZoomLevels }, get getMinimumCoverZoom() { return getMinimumCoverZoom }, get getViewThresholds() { return getViewThresholds }, get getBoundaryLabelCenter() { return getBoundaryLabelCenter }, get getStageStatusClass() { return getStageStatusClass }, get getMasterplanLotStatusClass() { return getMasterplanLotStatusClass }, get getMasterplanLotStatusLabel() { return getMasterplanLotStatusLabel }, get getMasterplanCssVars() { return getMasterplanCssVars }, get getMasterplanStatusBadgeStyle() { return getMasterplanStatusBadgeStyle }, get MASTERPLAN_STATUS_COLORS() { return MASTERPLAN_STATUS_COLORS }, get LotStatus() { return LotStatus }, get EstateType() { return EstateType }, get EstateAddon() { return EstateAddon }, get InteractiveMasterplanInterface() { return InteractiveMasterplanInterface }, get calculatePolygonCentroid() { return calculatePolygonCentroid }, get calculateBoundingBoxCenter() { return calculateBoundingBoxCenter }, get getLotLabelGap() { return getLotLabelGap }, get LOT_NUMBER_FONT_SIZE() { return LOT_NUMBER_FONT_SIZE }, get LOT_SIZE_FONT_SIZE() { return LOT_SIZE_FONT_SIZE }, get LOT_SIZE_DEFAULT_SCALE() { return LOT_SIZE_DEFAULT_SCALE } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})